import { useEffect, useState } from "react";
import { fetchX402Discovered, type X402CatalogSource, type X402DiscoveredEndpoint } from "../../lib/x402Discovery";

/**
 * Live x402 endpoints from external catalogs, beyond the ones recognized in
 * Boon. With no query this browses the broader network so a visitor can discover
 * new endpoints; with a query it searches by task. Picking one drops into the
 * recognize/review flow.
 */
export function X402DiscoveredResults({ query, onPick }: { query: string; onPick: (url: string) => void }) {
  const [endpoints, setEndpoints] = useState<X402DiscoveredEndpoint[]>([]);
  const [status, setStatus] = useState<"idle" | "loading" | "done">("idle");
  const [browseOpen, setBrowseOpen] = useState(false);
  const trimmed = query.trim();
  const browsing = trimmed.length < 2;

  useEffect(() => {
    if (!browsing) setBrowseOpen(false);
  }, [browsing]);

  useEffect(() => {
    if (browsing && !browseOpen) {
      setEndpoints([]);
      setStatus("idle");
      return;
    }
    let cancelled = false;
    setStatus("loading");
    fetchX402Discovered(browsing ? "" : trimmed)
      .then((found) => { if (!cancelled) { setEndpoints(found); setStatus("done"); } })
      .catch(() => { if (!cancelled) { setEndpoints([]); setStatus("done"); } });
    return () => { cancelled = true; };
  }, [trimmed, browsing, browseOpen]);

  if (browsing && !browseOpen) {
    return (
      <div className="mt-10 rounded-xl border border-faint bg-paper-deep/30 p-5 md:p-6">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-soft text-amber-deep" aria-hidden="true"><CatalogGlyph className="h-5 w-5" /></span>
          <div>
            <h3 className="text-lg font-display">Explore beyond the public trail</h3>
            <p className="mt-1 text-sm text-muted leading-relaxed max-w-3xl">Circle Marketplace and Coinbase Bazaar list tools that can accept x402 payments. These tools do not necessarily have a public Boon identity, use, review, or recognition signal.</p>
          </div>
        </div>
        <button type="button" className="btn btn-ghost mt-4 whitespace-nowrap" onClick={() => setBrowseOpen(true)}>Browse catalog tools</button>
      </div>
    );
  }

  if (status === "done" && endpoints.length === 0) return null;

  return (
    <div className="mt-6 pt-2">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-amber-soft text-amber-deep" aria-hidden="true"><CatalogGlyph className="h-4 w-4" /></span>
          <h3 className="text-lg font-display">{browsing ? "Catalog tools without a social signal" : "More tools matching your search"}</h3>
        </div>
        {browsing && (
          <button type="button" className="btn-mono text-xs text-muted hover:text-ink" onClick={() => setBrowseOpen(false)}>
            Hide catalog −
          </button>
        )}
      </div>
      <p className="mt-1 text-sm text-muted leading-relaxed max-w-3xl">
        {browsing
          ? "These tools can accept x402 payments, but their listing alone says nothing about who used them or whether they were useful."
          : "These tools match the task, but no linked identity has left an evidence-labeled review for them yet."}
      </p>
      {status === "loading" && endpoints.length === 0 ? (
        <p className="mt-3 text-sm text-muted">Reading the x402 network…</p>
      ) : (
        <ul className="mt-3 grid gap-2">
          {endpoints.map((endpoint) => (
            <li key={`${endpoint.routeId}:${endpoint.url}`}>
              <button
                type="button"
                onClick={() => onPick(endpoint.url)}
                className="w-full text-left border border-faint rounded-md px-4 py-3 hover:border-ink-soft transition-colors"
              >
                <span className="flex items-start justify-between gap-3">
                  <span className="flex min-w-0 items-center gap-2 text-sm text-ink font-display"><CatalogGlyph className="h-4 w-4 shrink-0 text-amber-deep" />{endpoint.service ?? endpoint.label}</span>
                  {endpoint.priceUsd && <span className="btn-mono text-xs text-muted shrink-0">${endpoint.priceUsd}</span>}
                </span>
                <span className="mt-0.5 block btn-mono text-xs text-olive-deep break-all">{endpoint.method} {endpoint.url}</span>
                {endpoint.catalogSources && endpoint.catalogSources.length > 0 && (
                  <span className="mt-1 block btn-mono text-[11px] text-muted">Listed by {endpoint.catalogSources.map(catalogSourceLabel).join(" + ")}</span>
                )}
                {endpoint.description && <span className="mt-1 block text-xs text-muted line-clamp-2">{endpoint.description}</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function catalogSourceLabel(source: X402CatalogSource): string {
  if (source === "circle_marketplace") return "Circle Marketplace";
  if (source === "coinbase_bazaar") return "Coinbase Bazaar";
  return "Poncho";
}

function CatalogGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
      <path d="M4 5h6v6H4zM14 5h6v6h-6zM4 15h6v4H4zM14 15h6v4h-6z" />
    </svg>
  );
}
